import { TemplatePicker } from "@/components/project/TemplatePicker";
import { formatNOK } from "@/lib/format";
import type { MatchedBudgetLine } from "@/lib/match-offer-line-templates";
import { useOfferLineTemplates } from "@/hooks/use-offers";
import type { MatchedBudget } from "@/components/new-project/PdfOfferUpload";
import { cn } from "@/lib/utils";
import { applyTemplateToLine } from "./apply-template-to-line";

interface Props {
  budget: MatchedBudget;
  onChange: (budget: MatchedBudget) => void;
}

/**
 * Steg 2 i import-dialogen: review + rett mal-match per linje før tilbudet
 * opprettes. Mal-match er et import-konsept og holdes her, ikke i
 * tilbudseditoren (VIB-1136).
 */
export function ImportLineReviewStep({ budget, onChange }: Props) {
  const { data: templates = [] } = useOfferLineTemplates();

  function updateLine(idx: number, next: MatchedBudgetLine) {
    const lines = budget.lines.map((l, i) => (i === idx ? next : l));
    onChange({
      ...budget,
      lines,
      unmatched_count: lines.filter((l) => l.template_match === null).length,
    });
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground">
        Kontroller fase og kostnadstype. Linjer uten mal er markert — velg riktig mal for å sette fase + intern/ekstern.
      </p>
      {budget.unmatched_count > 0 && (
        <p className="text-xs text-amber-700 font-medium">
          {budget.unmatched_count} linje{budget.unmatched_count !== 1 ? "r" : ""} mangler mal-kobling.
        </p>
      )}
      <ul className="space-y-1">
        {budget.lines.map((line, idx) => (
          <li
            key={idx}
            className={cn(
              "flex items-center gap-3 rounded-md border p-2 text-sm",
              line.template_match === null && "border-amber-300 bg-amber-50",
            )}
          >
            <span className="flex-1 min-w-0 truncate" title={line.description}>
              {line.description}
            </span>
            <span className="tabular-nums text-muted-foreground shrink-0">
              {formatNOK(line.amount)}
            </span>
            <span className="w-14 text-xs text-muted-foreground shrink-0">
              {line.effective_cost_type === "external" ? "Ekstern" : "Intern"}
            </span>
            <div className="w-44 shrink-0">
              <TemplatePicker
                templates={templates}
                currentId={line.template_match?.template_id ?? null}
                currentName={line.template_match?.template_name ?? null}
                onSelect={(t) => updateLine(idx, applyTemplateToLine(line, t))}
                variant="compact"
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
